import React from 'react';
import { Camera, Upload, Video, X, Image } from 'lucide-react';
import { Section, FormField } from './InspectionShared';

const PhotosVideosSection = ({
  formData, uploadLimits, uploadedFiles, blobUrls,
  videoUploadProgress, handleFileUpload, removeFile,
}) => {
  return (
    <Section title="Photos & Videos" icon={<Image className="h-6 w-6 text-purple-600" />}>
      <div className="text-xs text-stone-500 mb-2 flex items-center gap-4">
        <span>Photos: {formData.photos?.length || 0}/{uploadLimits.max_photos}</span>
        <span>Videos: {formData.videos?.length || 0}/{uploadLimits.max_videos}</span>
        {uploadLimits.bonus_photos > 0 && <span className="text-amber-600">(+{uploadLimits.bonus_photos} bonus from referrals)</span>}
      </div>
      <FormField label="Photos">
        <div className="space-y-3">
          <div className="flex gap-3 flex-wrap">
            <input type="file" accept="image/*" capture="environment" onChange={(e) => handleFileUpload(e, 'photo')} className="hidden" id="photo-camera-capture" />
            <label htmlFor="photo-camera-capture" className="cursor-pointer">
              <div className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-full font-medium transition-colors">
                <Camera className="h-5 w-5" /> Camera
              </div>
            </label>
            <input type="file" accept="image/*" multiple onChange={(e) => handleFileUpload(e, 'photo')} data-testid="photo-upload-input" className="hidden" id="photo-upload" />
            <label htmlFor="photo-upload" className="cursor-pointer">
              <div className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-full font-medium transition-colors">
                <Upload className="h-5 w-5" /> Upload Photos
              </div>
            </label>
          </div>
          {uploadedFiles.filter((f) => f.type === 'photo').length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {uploadedFiles.filter((f) => f.type === 'photo').map((file) => (
                <div key={file.file_id} className="relative group">
                  {blobUrls[file.file_id] ? (
                    <img src={blobUrls[file.file_id]} alt={file.name} className="w-full h-32 object-cover rounded-xl border-2 border-stone-200" />
                  ) : (
                    <div className="w-full h-32 rounded-xl border-2 border-stone-200 bg-stone-100 flex items-center justify-center">
                      <div className="animate-spin h-5 w-5 border-2 border-amber-500 border-t-transparent rounded-full" />
                    </div>
                  )}
                  <button type="button" onClick={() => removeFile(file.file_id, 'photo')} className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`remove-photo-${file.file_id}`}>
                    <X className="h-4 w-4" />
                  </button>
                  {file.section && <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-xs px-2 py-1 rounded-b-xl truncate">{file.section}</div>}
                  {!file.section && <div className="text-xs text-stone-600 mt-1 truncate">{file.name}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      </FormField>

      <FormField label="Videos">
        <div className="space-y-3">
          <div className="flex gap-3 flex-wrap">
            <input type="file" accept="video/*" capture="environment" onChange={(e) => handleFileUpload(e, 'video')} className="hidden" id="video-camera-capture" />
            <label htmlFor="video-camera-capture" className="cursor-pointer">
              <div className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-full font-medium transition-colors">
                <Video className="h-5 w-5" /> Record
              </div>
            </label>
            <input type="file" accept="video/*" onChange={(e) => handleFileUpload(e, 'video')} className="hidden" id="video-upload" />
            <label htmlFor="video-upload" className="cursor-pointer">
              <div className="flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 rounded-full font-medium transition-colors">
                <Upload className="h-5 w-5" /> Upload Video
              </div>
            </label>
          </div>
          {videoUploadProgress && (
            <div className="bg-orange-50 border border-orange-200 rounded-xl p-3 space-y-2" data-testid="video-upload-progress">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium text-orange-700">{videoUploadProgress.status}</span>
                <span className="text-orange-600 font-bold">{videoUploadProgress.percent}%</span>
              </div>
              <div className="w-full h-2 bg-orange-100 rounded-full overflow-hidden">
                <div className="h-full bg-orange-500 rounded-full transition-all duration-300" style={{ width: `${videoUploadProgress.percent}%` }} />
              </div>
            </div>
          )}
          {uploadedFiles.filter((f) => f.type === 'video').length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {uploadedFiles.filter((f) => f.type === 'video').map((file) => (
                <div key={file.file_id} className="relative group">
                  <div className="w-full h-32 rounded-xl border-2 border-stone-200 bg-stone-800 flex items-center justify-center">
                    <Video className="h-8 w-8 text-stone-400" />
                  </div>
                  <button type="button" onClick={() => removeFile(file.file_id, 'video')} className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <X className="h-4 w-4" />
                  </button>
                  <div className="text-xs text-stone-600 mt-1 truncate">{file.name}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </FormField>
    </Section>
  );
};

export default PhotosVideosSection;
